import queryString from 'query-string'
import { useEffect, useMemo, useState } from 'react'

import { createManifoldAggregate } from '../util'
import { useSiteMonitoring } from '@api/useSiteMonitoring'
import type { useTankRundownProps, useTankRundownReturnType, TankRundownData } from '../types'

const initializeSelectedManifold = (store: Gravitate.Site): Gravitate.Tank => {
  const manifoldGroup = store.grouped_tanks.find((group) =>
    group.manifold_tanks.some(
      (manifold: Gravitate.SupplyOptionTank) => manifold.manifold_id === store.tanks[0].manifold_id
    )
  )
  const manifold = manifoldGroup?.manifold_tanks?.find(
    (manifold: Gravitate.SupplyOptionTank) => manifold.manifold_id === store.tanks[0].manifold_id
  )

  return createManifoldAggregate(manifold)
}

const initializeSelectedTank = (store: Gravitate.Site): Gravitate.Tank => {
  const linkTankId = queryString.parse(window.location.search).tank_id
  let defaultTank = store?.tanks.find((tank) => tank.tank_id && tank.tank_id.toString() === linkTankId)
  if (!defaultTank) {
    defaultTank = store?.tanks[0]
  }

  return defaultTank
}

export const useTankRundown = ({
  store,
  shouldDefaultTankSelection = true,
}: useTankRundownProps): useTankRundownReturnType => {
  const [tankRundownData, setTankRundownData] = useState<TankRundownData | undefined>()
  const [selectedTank, setSelectedTank] = useState<Gravitate.Tank | undefined>(undefined)
  const [selectedManifold, setSelectedManifold] = useState<Gravitate.Tank | undefined>()
  const { useTankRundownQuery } = useSiteMonitoring()
  const payload = useMemo(
    () =>
      selectedManifold
        ? {
            store_number: selectedManifold.store_number,
            tank_id: selectedManifold.manifold_id,
          }
        : {
            store_number: selectedTank?.store_number || '',
            tank_id: selectedTank?.tank_id || '',
          },
    [selectedManifold, selectedTank]
  )
  const { data: tankQueryData, isPending, isFetching } = useTankRundownQuery(payload.store_number, payload.tank_id)
  useEffect(() => {
    if (!store || store.monitoring_strategy === 'No Readings (Call-In)') return
    if (store.tanks[0]?.manifold_id && shouldDefaultTankSelection) {
      const manifold = initializeSelectedManifold(store)
      setSelectedManifold(manifold)
    } else if (shouldDefaultTankSelection) {
      const defaultTank = initializeSelectedTank(store)
      setSelectedTank(defaultTank)
    }
  }, [store])

  useEffect(() => {
    if (selectedTank) {
      setSelectedManifold(undefined)
    }
  }, [selectedTank])

  useEffect(() => {
    if (selectedManifold) {
      setSelectedTank(undefined)
    }
  }, [selectedManifold])

  useEffect(() => {
    if ((!selectedManifold && !selectedTank) || !store) return
    if (tankQueryData) {
      setTankRundownData(tankQueryData)
    }
  }, [tankQueryData, selectedTank, selectedManifold, isPending, isFetching])

  return { selectedTank, setSelectedTank, selectedManifold, setSelectedManifold, tankRundownData, setTankRundownData }
}
